import { useMemo, useRef, useState } from "react";
import { LockOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Tabs, Tag, message } from "antd";

import PageHeader from "@/components/UI/PageHeader";
import SimpleTableTemplate from "@/components/Templates/SimpleTableTemplate";
import ViewSwitcher from "@/components/UI/TableToolbar/ViewSwitcher";
import { useSelectableTableView, useTableHeight } from "@/utils/tableHelpers";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useFetch, is403ApiError } from "@/hooks/FetchHook";
import useTableState from "@/hooks/useTableState";
import { createDefaultTableState } from "@/utils/workspaceState";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import {
  constructQueryString,
  prepareUnifiedDataPostRequestBody,
} from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import PaginatedTable from "@/components/UI/PaginatedTable";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import Loader from "@/components/UI/Loader";

import { useUser } from "@/contexts/UserContext";
import { getUserRoleForTrial } from "@/components/Insights/insights.utils";

import { MilestoneDrawer } from "@/components/PlanOfRecord/MilestoneDrawer";
import { MilestoneGantt } from "@/components/PlanOfRecord/MilestoneGantt";
import { DeleteMilestoneModal } from "@/components/PlanOfRecord/DeleteMilestoneModal";
import { MilestoneFormModal } from "@/components/PlanOfRecord/MilestoneFormModal";

import {
  buildChangeHistoryColumns,
  buildMilestoneHistoryRows,
} from "./changeHistoryTableConfig";
import { createMilestoneColumns } from "./milestonesTableConfig";
import { can, hasPlanEditorRole, type PlanUser } from "./planAccessData";
import {
  PLAN_OF_RECORD_DATASET_TYPE,
  buildCreateMilestonePayload,
  buildUpdateMilestonePayload,
  mapMilestoneRowToMilestone,
} from "./planOfRecordMapping";
import type {
  PlanOfRecordMetadata,
  PlanOfRecordMilestone,
  PlanOfRecordMilestoneData,
  PlanOfRecordMilestoneRow,
} from "./planOfRecord.types";
import { usePlanOfRecordMutations } from "@/hooks/usePlanOfRecordMutations";

/** View perspectives on the same milestones — same ViewSwitcher every dashboard-studio
 * table/plot toggle uses, not a bespoke one. */
const MILESTONE_VIEWS = [
  { label: "Table", value: "table" },
  { label: "Timeline", value: "gantt" },
];

/** Real plans hold a few dozen milestones; one page comfortably covers a whole plan. */
const FULL_LIST_PAGE_SIZE = 500;

/**
 * Trial Plan of Record (in-flight) — the sponsor-owned plan for a trial already in conduct,
 * successor to the pre-live Trial Planning module.
 *
 * Backed by the `plan-of-record-milestones` dataset. Design reasoning in
 * docs/PRD_MOCKUPS_DESIGN_NOTES.md.
 */
export default function PlanOfRecord() {
  const queryString = constructQueryString({
    baseParams: { dataset_type: PLAN_OF_RECORD_DATASET_TYPE },
  });

  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useFetch({ endpointSuffix: `filters/${queryString}` });

  if (metadataError) {
    return (
      <div className="flex mt-25 justify-center items-center">
        {is403ApiError(metadataError) ? <Error403 /> : <ErrorComponent />}
      </div>
    );
  }

  if (metadataLoading || !metadata) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return <PlanOfRecordContent metadata={metadata as PlanOfRecordMetadata} />;
}

function PlanOfRecordContent({ metadata }: { metadata: PlanOfRecordMetadata }) {
  const { id } = useScopeParams();
  const [formMode, setFormMode] = useState<"create" | "edit" | null>(null);
  const [milestoneView, setMilestoneView] = useState(MILESTONE_VIEWS[0].value);
  const [activeTab, setActiveTab] = useState("milestones");
  const [editing, setEditing] = useState<PlanOfRecordMilestone | null>(null);
  const [deleting, setDeleting] = useState<PlanOfRecordMilestone | null>(null);

  // Real identity, not the mockup harness: MockControlsProvider is only mounted under the
  // standalone /mockup-preview.html entry point, never on this routed screen, so
  // useMockControls()'s currentUserId here was always its default (null) — every real
  // user resolved to PLAN_USERS[0] (Dana Whitfield), permanently, on every account. That
  // made canEdit permanently true and every "Recorded against" attribution wrong.
  const { userInfo } = useUser();
  const grantedRole = getUserRoleForTrial(userInfo, id);
  const currentUser: PlanUser = useMemo(
    () => ({
      id: userInfo?.id ?? "",
      name: userInfo
        ? `${userInfo.first_name} ${userInfo.last_name}`.trim() ||
          userInfo.email
        : "",
      jobTitle: "",
      roleId: hasPlanEditorRole(grantedRole) ? "plan-editor" : "plan-viewer",
      grantedVia: "",
      grantedAt: "",
    }),
    [userInfo, grantedRole],
  );
  // Client-side only, same as everywhere else in this app — the actual write gate is the
  // server-side plan_editor role check in views_plan_of_record.py. This only controls
  // which affordances render; a user without the role gets a 403 from the API either way.
  const canEdit = can(currentUser, "plan.edit");

  const { createMilestone, updateMilestone, deleteMilestone } =
    usePlanOfRecordMutations();

  const pageRef = useRef<HTMLDivElement>(null);
  const { tableHeight: maxMenuHeight } = useTableHeight({ tableRef: pageRef });

  const metadataColumns = useMemo(
    () => metadata.columns.map((column) => column.field),
    [metadata.columns],
  );
  const defaultHiddenColumns = useMemo(
    () =>
      metadata.columns
        .filter((column) => Boolean(column.hidden))
        .map((column) => column.field),
    [metadata.columns],
  );
  const metadataFilterKeys = useMemo(
    () => Object.keys(metadata.filters || {}),
    [metadata.filters],
  );

  const initialTableState = useMemo(() => {
    const base = createDefaultTableState(metadataColumns, defaultHiddenColumns);
    return {
      ...base,
      pagination: { ...base.pagination, total: metadata.row_count },
    };
  }, [metadataColumns, defaultHiddenColumns, metadata.row_count]);

  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: initialTableState,
    pageName: PLAN_OF_RECORD_DATASET_TYPE,
    isSingleSubject: false,
    trialId: id,
    metadataColumns,
    metadataFilterKeys,
  });

  const allFiltersData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(metadata.filters || {}).map(([key, filterInfo]) => [
          key,
          {
            data_type: filterInfo.data_type || "text",
            values: filterInfo.values || [],
          },
        ]),
      ),
    [metadata.filters],
  );

  const payload = useMemo(
    () =>
      prepareUnifiedDataPostRequestBody({
        global_filters: currentTableState.filters,
        allFiltersData,
        page: currentTableState.pagination.current,
        pageSize: currentTableState.pagination.pageSize,
        sorting: currentTableState.sorting,
      }),
    [
      currentTableState.filters,
      currentTableState.pagination,
      currentTableState.sorting,
      allFiltersData,
    ],
  );

  const {
    data: milestoneDataRaw,
    isLoading: loadingMilestoneData,
    error: milestoneDataError,
  } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: PLAN_OF_RECORD_DATASET_TYPE, ...payload },
    callback: (data) => {
      if (currentTableState.pagination.total !== data.count) {
        tableStateFunctions.setPagination({
          ...currentTableState.pagination,
          total: data.count,
        });
      }
    },
  });
  const milestoneData = milestoneDataRaw as
    | PlanOfRecordMilestoneData
    | undefined;

  const fullListPayload = useMemo(
    () =>
      prepareUnifiedDataPostRequestBody({
        global_filters: {},
        allFiltersData,
        page: 1,
        pageSize: FULL_LIST_PAGE_SIZE,
        sorting: [{ field: "planned_date", direction: "ascending" }],
      }),
    [allFiltersData],
  );

  // Only the Timeline view and Change history tab need every milestone — the Table view
  // reads the paginated fetch above. Firing this unconditionally on every mount meant a
  // second full dataset request nobody was looking at yet, every single time.
  const needsFullList =
    activeTab === "history" ||
    (activeTab === "milestones" && milestoneView === "gantt");

  const { data: fullMilestonesDataRaw } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: PLAN_OF_RECORD_DATASET_TYPE, ...fullListPayload },
    enabled: needsFullList,
  });
  const fullMilestoneRows = useMemo(
    () =>
      (fullMilestonesDataRaw as PlanOfRecordMilestoneData | undefined)
        ?.results ?? [],
    [fullMilestonesDataRaw],
  );
  const ganttMilestones = useMemo(
    () => fullMilestoneRows.map(mapMilestoneRowToMilestone),
    [fullMilestoneRows],
  );
  const historyRows = useMemo(
    () => buildMilestoneHistoryRows(fullMilestoneRows),
    [fullMilestoneRows],
  );
  const historyColumns = useMemo(() => buildChangeHistoryColumns(), []);

  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<PlanOfRecordMilestoneRow>();
  const selectedMilestone = selectedRecord
    ? mapMilestoneRowToMilestone(selectedRecord)
    : null;

  const columns = useMemo(
    () =>
      createMilestoneColumns(
        currentTableState.columns
          .filter(
            (column: ColumnsType) =>
              column.visibility === ColumnVisibility.VISIBLE,
          )
          .map((column: ColumnsType) => column.fieldName),
        currentTableState.lockedColumns,
        canEdit
          ? {
              canEdit,
              onEdit: (milestone) => {
                setEditing(milestone);
                setFormMode("edit");
              },
              onDelete: setDeleting,
            }
          : undefined,
      ),
    [currentTableState.columns, currentTableState.lockedColumns, canEdit],
  );

  const header = (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <PageHeader
          title="Plan of Record"
          tooltipText="The sponsor-owned plan for this trial: planned milestones and enrolment targets, with provenance and a full change history. Vendor projections are shown alongside for reference — your plan is what expected-vs-actual compares against."
        />
        {!canEdit ? (
          <Tag icon={<LockOutlined />} color="default">
            Read only
          </Tag>
        ) : null}
      </div>

      {canEdit ? (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setFormMode("create")}
        >
          Add milestone
        </Button>
      ) : null}
    </div>
  );

  return (
    <div ref={pageRef} className="flex w-full min-w-0 flex-col gap-4 p-4">
      {header}

      <Tabs
        className="w-full"
        destroyOnHidden
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "milestones",
            label: `Milestones (${metadata.row_count})`,
            children: (
              <div className="flex flex-col gap-3">
                {/* Same slot every dashboard-studio table/plot toggle renders through, so
                    it stays visible across view changes instead of disappearing with the
                    table it belongs to. */}
                <TableToolbar
                  currentTableState={currentTableState}
                  tableStateFunctions={tableStateFunctions}
                  tableMetadata={metadata}
                  maxMenuHeight={maxMenuHeight}
                  fullPageName={PLAN_OF_RECORD_DATASET_TYPE}
                  endpointSuffix={BASE_ENDPOINT}
                  name={PLAN_OF_RECORD_DATASET_TYPE}
                  datasetName={PLAN_OF_RECORD_DATASET_TYPE}
                  viewSwitcher={
                    <ViewSwitcher
                      screen={milestoneView}
                      setScreen={setMilestoneView}
                      options={MILESTONE_VIEWS}
                    />
                  }
                />

                {milestoneView === "gantt" ? (
                  <>
                    {metadata.row_count > FULL_LIST_PAGE_SIZE ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={`Showing the first ${FULL_LIST_PAGE_SIZE} of ${metadata.row_count} milestones.`}
                      />
                    ) : null}
                    <MilestoneGantt milestones={ganttMilestones} />
                  </>
                ) : (
                  <>
                    <AppliedFiltersSection
                      filters={currentTableState.filters}
                      clearFilters={() => tableStateFunctions.setFilters({})}
                      setFilters={tableStateFunctions.setFilters}
                      filterOptions={metadata.filters}
                      allFiltersData={metadata.filters}
                    />
                    {milestoneDataError ? (
                      <ErrorComponent />
                    ) : (
                      <PaginatedTable
                        data={milestoneData?.results}
                        columns={columns}
                        loading={loadingMilestoneData}
                        setPagination={tableStateFunctions.setPagination}
                        pagination={currentTableState.pagination}
                        rowKey="id"
                        onRow={getClickableRowProps}
                      />
                    )}
                  </>
                )}
              </div>
            ),
          },
          {
            key: "history",
            label: `Change history (${historyRows.length})`,
            children: (
              <div className="flex flex-col gap-3">
                {metadata.row_count > FULL_LIST_PAGE_SIZE ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Showing history for the first ${FULL_LIST_PAGE_SIZE} of ${metadata.row_count} milestones.`}
                  />
                ) : null}
                <SimpleTableTemplate
                  fileName="plan-of-record-change-history"
                  data={historyRows}
                  columns={historyColumns}
                  rowKey="id"
                  inertDownloadButton
                  hideReturnToDefault
                  enableColumnSelector
                />
              </div>
            ),
          },
        ]}
      />

      <MilestoneDrawer
        milestone={selectedMilestone}
        canEdit={canEdit}
        requiredRoleName="Plan editor"
        onEdit={(milestone) => {
          setSelectedRecord(null);
          setEditing(milestone);
          setFormMode("edit");
        }}
        onClose={() => setSelectedRecord(null)}
      />

      <DeleteMilestoneModal
        milestone={deleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async (rationale) => {
          if (!deleting) return;
          try {
            await deleteMilestone({ milestoneId: deleting.id, rationale });
            message.success(`“${deleting.label}” deleted.`);
          } catch {
            message.error("Couldn't delete the milestone. Try again.");
          } finally {
            setDeleting(null);
          }
        }}
      />

      <MilestoneFormModal
        mode={formMode}
        milestone={editing}
        currentUser={currentUser}
        onCancel={() => {
          setFormMode(null);
          setEditing(null);
        }}
        onSave={async (milestoneId, edit, rationale) => {
          try {
            await updateMilestone({
              milestoneId,
              payload: buildUpdateMilestonePayload(edit, rationale),
            });
            setFormMode(null);
            setEditing(null);
            message.success(
              `Change saved and recorded against ${currentUser.name}.`,
            );
          } catch {
            message.error("Couldn't save the change. Try again.");
          }
        }}
        onCreate={async (draft, rationale) => {
          try {
            await createMilestone(
              buildCreateMilestonePayload(draft, rationale),
            );
            setFormMode(null);
            message.success(
              `“${draft.label}” added and recorded against ${currentUser.name}.`,
            );
          } catch {
            message.error("Couldn't add the milestone. Try again.");
          }
        }}
      />
    </div>
  );
}
